import { useState, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { templates, type ShopTemplate } from "@/data/shopTemplates";

const PreviewCard = ({ template, index, isInView }: { template: ShopTemplate; index: number; isInView: boolean }) => {
  const [imgIdx, setImgIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const count = template.images.length;

  const go = (next: number, direction: number) => {
    setDir(direction);
    setImgIdx(next);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: 0.15 + index * 0.1, duration: 0.5 }}
      className="group flex flex-col"
    >
      {/* Image container — matches CaseStudyCard style */}
      <div className="relative overflow-hidden aspect-[4/3] bg-neutral-800">
        <AnimatePresence mode="popLayout" initial={false} custom={dir}>
          <motion.img
            key={imgIdx}
            src={template.images[imgIdx]}
            alt={`${template.title} template preview`}
            className="w-full h-full object-cover absolute inset-0"
            loading="lazy"
            decoding="async"
            custom={dir}
            initial={{ opacity: 0, x: dir * 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dir * -40 }}
            transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
          />
        </AnimatePresence>

        {/* Price tag */}
        <span className="absolute top-2 left-2 z-[5] text-[9px] md:text-[10px] font-body tracking-[0.08em] uppercase text-white/90 bg-black/40 backdrop-blur-sm rounded-full py-[4px] px-[12px]">
          {template.priceDisplay}
        </span>

        {/* Desktop hover arrows */}
        {count > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); go((imgIdx - 1 + count) % count, -1); }}
              aria-label="Previous image"
              className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white/20 z-10"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); go((imgIdx + 1) % count, 1); }}
              aria-label="Next image"
              className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white/20 z-10"
            >
              <ChevronRight size={14} />
            </button>
          </>
        )}

        {/* Dot indicators */}
        {count > 1 && (
          <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex gap-1.5 z-[5]">
            {template.images.map((_, j) => (
              <button
                key={j}
                onClick={(e) => { e.stopPropagation(); go(j, j > imgIdx ? 1 : -1); }}
                aria-label={`View image ${j + 1}`}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  j === imgIdx ? "bg-white scale-125" : "bg-white/40 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Text underneath — matches CaseStudyCard layout */}
      <div className="mt-3 space-y-0.5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-body text-ts-sm font-medium text-foreground/90 group-hover:text-foreground transition-colors">
            {template.title}
          </h3>
        </div>
        <p className="font-body text-ts-xs font-normal pb-[8px] text-muted-foreground">
          {template.subtitle}
        </p>
        <div className="flex items-center gap-4 border-t border-border pt-2 mt-1.5">
          <a
            href={template.purchaseUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-body text-[0.625rem] font-normal tracking-[0.15em] uppercase text-muted-foreground hover:text-foreground transition-colors"
          >
            Buy
            <ArrowUpRight size={9} />
          </a>
          <a
            href={template.livePreviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-body text-[0.625rem] font-normal tracking-[0.15em] uppercase text-muted-foreground hover:text-foreground transition-colors"
          >
            Preview
            <ExternalLink size={9} />
          </a>
        </div>
      </div>
    </motion.article>
  );
};

const ShopSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  /* Show first 3 templates as compact cards */
  const preview = templates.slice(0, 3);

  return (
    <section
      id="shop"
      ref={sectionRef}
      className="section-light px-5 md:px-10 lg:px-14 py-16 md:py-24"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        <p className="font-body text-ts-2xs font-medium tracking-[0.25em] uppercase text-muted-foreground mb-3">
          Shop
        </p>
        <h2 className="font-display text-4xl md:text-5xl italic text-foreground mb-3">
          Templates
        </h2>
        <p className="font-body text-ts-sm text-muted-foreground tracking-wide max-w-[520px] mb-12 md:mb-16">
          Production-ready templates built with the same structure and attention we bring to client
          work. Plug in your brand and launch.
        </p>
      </motion.div>

      {/* Compact card grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-[1100px]">
        {preview.map((template, i) => (
          <PreviewCard key={template.slug} template={template} index={i} isInView={isInView} />
        ))}
      </div>

      {/* View all CTA */}
      <div className="pt-10">
        <Link
          to="/shop"
          className="inline-flex items-center gap-1.5 font-body text-ts-2xs tracking-[0.1em] uppercase text-muted-foreground hover:text-foreground transition-colors border-b border-border hover:border-foreground/50 pb-0.5"
        >
          View All Templates
          <span className="text-[10px]">→</span>
        </Link>
      </div>
    </section>
  );
};

export default ShopSection;
